import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Award,
  Target,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Briefcase,
  Crown,
  Star,
  Share2,
  MessageSquare,
  Settings,
  Globe,
  Facebook,
  Instagram,
  Twitter,
  Linkedin,
  Copy,
  Eye,
  Download
} from "lucide-react";
import MLMDashboard from "../mlm/MLMDashboard";
import AIChat from "../chat/AIChat";
import { mockSocialMediaProfiles } from "../../mock";

const EnhancedResellerDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [chatOpen, setChatOpen] = useState(false);
  const [reseller, setReseller] = useState(null);
  const [socialProfiles, setSocialProfiles] = useState(mockSocialMediaProfiles);
  const [resellerPermissions, setResellerPermissions] = useState({
    customizeTheme: true,
    manageSocialMedia: true,
    viewAnalytics: true,
    customerSupport: true,
    createContent: false, // Limited by admin
    modifyPricing: false, // Limited by admin
    accessReports: true,
    teamManagement: true
  });

  const [storeCustomization, setStoreCustomization] = useState({
    storeName: "John's Business Cards",
    primaryColor: "#10b981",
    secondaryColor: "#3b82f6",
    logoUrl: "",
    welcomeMessage: "Welcome to my business card store! Get professional cards at wholesale prices.",
    contactInfo: {
      phone: "+1-555-0123",
      email: "john@example.com",
      website: "www.johnscards.com"
    }
  });

  const [customerChats, setCustomerChats] = useState([
    {
      id: 1,
      customerName: "Sarah Wilson",
      lastMessage: "Can you help me with template selection?",
      timestamp: "2024-01-30T14:30:00Z",
      status: "active",
      channel: "website"
    },
    {
      id: 2,
      customerName: "Mike Johnson",
      lastMessage: "What's the delivery time for business cards?",
      timestamp: "2024-01-30T13:45:00Z", 
      status: "resolved",
      channel: "website"
    }
  ]);

  useEffect(() => {
    // Check if user is logged in and is a reseller
    const customerAuth = localStorage.getItem('customerAuth');
    if (!customerAuth) {
      navigate('/login');
      return;
    }
    
    const userData = JSON.parse(customerAuth);
    if (userData.accountType !== 'reseller') {
      navigate('/reseller-upgrade');
      return;
    }
    
    setReseller(userData);
  }, [navigate]);

  const handleSocialConnect = async (platform) => {
    // Mock social media connection
    const updated = socialProfiles.map(profile => 
      profile.platform.toLowerCase() === platform.toLowerCase()
        ? { ...profile, isConnected: true, lastPost: new Date().toISOString().split('T')[0] }
        : profile
    );
    setSocialProfiles(updated);
  };

  const handleSocialPost = async (platform, content) => {
    // Mock posting to social media
    console.log(`Posting to ${platform}:`, content);
    // Update last post date
    const updated = socialProfiles.map(profile => 
      profile.platform.toLowerCase() === platform.toLowerCase()
        ? { ...profile, lastPost: new Date().toISOString().split('T')[0] }
        : profile
    );
    setSocialProfiles(updated);
  };

  const copyReferralLink = () => {
    const referralLink = `https://cardmaster.com/ref/${reseller?.id || 'USER123'}`;
    navigator.clipboard.writeText(referralLink);
    // Toast notification would go here
  };

  const stats = [
    {
      title: "Total Earnings",
      value: "$3,245.75",
      change: "+12.3%",
      icon: <DollarSign className="h-6 w-6 text-green-600" />,
      color: "green"
    },
    {
      title: "This Month",
      value: "$1,247.50",
      change: "+8.2%",
      icon: <TrendingUp className="h-6 w-6 text-blue-600" />,
      color: "blue"
    },
    {
      title: "Team Size",
      value: "47",
      change: "+3",
      icon: <Users className="h-6 w-6 text-purple-600" />,
      color: "purple"
    },
    {
      title: "Active Customers",
      value: "128",
      change: "+15.7%",
      icon: <Target className="h-6 w-6 text-orange-600" />,
      color: "orange"
    }
  ];

  if (!reseller) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="bg-gradient-to-r from-emerald-500 to-blue-600 p-2 rounded-lg">
                  <Crown className="h-6 w-6 text-white" />
                </div>
                <div>
                  <span className="text-xl font-bold text-gray-900">{storeCustomization.storeName}</span>
                  <div className="text-xs text-gray-500">Reseller Dashboard</div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white">
                Gold Level
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                Customer View
              </Button>
              <Button variant="ghost" size="sm">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome back, {reseller.name}!</h1>
          <p className="text-gray-600">Manage your reseller business and grow your network</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7 bg-white shadow-sm border mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="mlm">MLM</TabsTrigger>
            <TabsTrigger value="customers">Customers</TabsTrigger>
            <TabsTrigger value="store">My Store</TabsTrigger>
            <TabsTrigger value="social">Social Media</TabsTrigger>
            <TabsTrigger value="chat">Support Chat</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {/* Stats Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                        <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                        <p className={`text-sm text-${stat.color}-600 flex items-center mt-1`}>
                          <TrendingUp className="h-3 w-3 mr-1" />
                          {stat.change}
                        </p>
                      </div>
                      <div className={`bg-${stat.color}-100 p-3 rounded-lg`}>
                        {stat.icon}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <Button className="w-full justify-start" variant="outline">
                      <Share2 className="h-4 w-4 mr-2" />
                      Share Referral Link
                    </Button>
                    <Button className="w-full justify-start" variant="outline">
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Customer Support Center
                    </Button>
                    <Button className="w-full justify-start" variant="outline">
                      <Download className="h-4 w-4 mr-2" />
                      Marketing Materials
                    </Button>
                    <Button className="w-full justify-start" variant="outline">
                      <Globe className="h-4 w-4 mr-2" />
                      Social Media Automation
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Referral Link</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <div className="flex">
                        <input 
                          type="text" 
                          value={`https://cardmaster.com/ref/${reseller.id || 'USER123'}`}
                          className="flex-1 p-3 border rounded-l text-sm bg-gray-50"
                          readOnly
                        />
                        <Button className="rounded-l-none" onClick={copyReferralLink}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-emerald-50 to-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-2">Your Earnings Potential</h4>
                      <div className="text-sm text-gray-700 space-y-1">
                        <p>• 25% commission on direct sales</p>
                        <p>• Multi-level bonuses up to 5 levels deep</p>
                        <p>• Monthly volume bonuses</p>
                        <p>• Achievement level rewards</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* MLM Tab */}
          <TabsContent value="mlm">
            <MLMDashboard userType="reseller" />
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Management</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">Recent Customer Activity</h3>
                      <p className="text-sm text-gray-600">Track your customer interactions and sales</p>
                    </div>
                    <Button>
                      <Eye className="h-4 w-4 mr-2" />
                      View All Customers
                    </Button>
                  </div>

                  <div className="grid gap-4">
                    {customerChats.map((chat) => (
                      <div key={chat.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                        <div className="flex items-center space-x-4">
                          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                            {chat.customerName.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <h4 className="font-semibold">{chat.customerName}</h4>
                            <p className="text-sm text-gray-600">{chat.lastMessage}</p>
                            <p className="text-xs text-gray-500">{new Date(chat.timestamp).toLocaleString()}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3">
                          <Badge variant={chat.status === 'active' ? 'default' : 'secondary'}>
                            {chat.status}
                          </Badge>
                          <Button size="sm" onClick={() => setChatOpen(true)}>
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Reply
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Store Tab */}
          <TabsContent value="store" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Store Customization</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {resellerPermissions.customizeTheme ? (
                    <>
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Store Name</label>
                            <Input
                              value={storeCustomization.storeName}
                              onChange={(e) => setStoreCustomization(prev => ({
                                ...prev,
                                storeName: e.target.value
                              }))}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">Primary Color</label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="color"
                                value={storeCustomization.primaryColor}
                                onChange={(e) => setStoreCustomization(prev => ({
                                  ...prev,
                                  primaryColor: e.target.value
                                }))}
                                className="w-12 h-12 rounded border cursor-pointer"
                              />
                              <Input
                                value={storeCustomization.primaryColor}
                                onChange={(e) => setStoreCustomization(prev => ({
                                  ...prev,
                                  primaryColor: e.target.value
                                }))}
                                className="font-mono"
                              />
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">Secondary Color</label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="color"
                                value={storeCustomization.secondaryColor}
                                onChange={(e) => setStoreCustomization(prev => ({
                                  ...prev,
                                  secondaryColor: e.target.value
                                }))}
                                className="w-12 h-12 rounded border cursor-pointer"
                              />
                              <Input
                                value={storeCustomization.secondaryColor}
                                onChange={(e) => setStoreCustomization(prev => ({
                                  ...prev,
                                  secondaryColor: e.target.value
                                }))}
                                className="font-mono"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium mb-2">Welcome Message</label>
                            <textarea
                              value={storeCustomization.welcomeMessage}
                              onChange={(e) => setStoreCustomization(prev => ({
                                ...prev,
                                welcomeMessage: e.target.value
                              }))}
                              className="w-full p-3 border rounded-md"
                              rows={4}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">Contact Phone</label>
                            <Input
                              value={storeCustomization.contactInfo.phone}
                              onChange={(e) => setStoreCustomization(prev => ({
                                ...prev,
                                contactInfo: { ...prev.contactInfo, phone: e.target.value }
                              }))}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium mb-2">Contact Email</label>
                            <Input
                              value={storeCustomization.contactInfo.email}
                              onChange={(e) => setStoreCustomization(prev => ({
                                ...prev,
                                contactInfo: { ...prev.contactInfo, email: e.target.value }
                              }))}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="pt-6 border-t">
                        <Button className="bg-gradient-to-r from-emerald-500 to-blue-600 hover:from-emerald-600 hover:to-blue-700">
                          Save Store Settings
                        </Button>
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-8">
                      <Settings className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Limited Customization Access</h3>
                      <p className="text-gray-600">Your administrator has limited store customization features. Contact support for more options.</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Social Media Tab */}
          <TabsContent value="social" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Social Media Management</CardTitle>
              </CardHeader>
              <CardContent>
                {resellerPermissions.manageSocialMedia ? (
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      {socialProfiles.map((profile) => (
                        <div key={profile.id} className="p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center space-x-3">
                              {profile.platform === 'Facebook' && <Facebook className="h-6 w-6 text-blue-600" />}
                              {profile.platform === 'Instagram' && <Instagram className="h-6 w-6 text-pink-600" />}
                              {profile.platform === 'LinkedIn' && <Linkedin className="h-6 w-6 text-blue-700" />}
                              {profile.platform === 'Twitter' && <Twitter className="h-6 w-6 text-sky-500" />}
                              <div>
                                <h3 className="font-semibold">{profile.platform}</h3>
                                <p className="text-sm text-gray-600">{profile.username}</p>
                              </div>
                            </div>
                            <Badge variant={profile.isConnected ? 'default' : 'secondary'}>
                              {profile.isConnected ? 'Connected' : 'Not Connected'}
                            </Badge>
                          </div>

                          {profile.isConnected ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between text-sm">
                                <span>Auto-posting:</span>
                                <Switch
                                  checked={profile.autoPost}
                                  onCheckedChange={(checked) => {
                                    const updated = socialProfiles.map(p => 
                                      p.id === profile.id ? { ...p, autoPost: checked } : p
                                    );
                                    setSocialProfiles(updated);
                                  }}
                                />
                              </div>
                              <div className="text-sm text-gray-600">
                                Last post: {profile.lastPost || 'Never'}
                              </div>
                              <Button 
                                size="sm" 
                                className="w-full"
                                onClick={() => handleSocialPost(profile.platform, "Check out our professional business cards!")}
                              >
                                Post Now
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              className="w-full"
                              onClick={() => handleSocialConnect(profile.platform)}
                            >
                              Connect {profile.platform}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-900 mb-2">Social Media Automation</h4>
                      <p className="text-sm text-blue-800 mb-3">
                        Automatically share your business card designs and promotions across your social media platforms.
                      </p>
                      <div className="text-sm text-blue-700 space-y-1">
                        <p>• Schedule daily product showcases</p>
                        <p>• Share customer success stories</p>
                        <p>• Promote special discounts and offers</p>
                        <p>• Build your personal brand as a reseller</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Globe className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Social Media Access Restricted</h3>
                    <p className="text-gray-600">Your administrator has disabled social media management. Contact support to enable this feature.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Chat Tab */}
          <TabsContent value="chat" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Support Center</CardTitle>
              </CardHeader>
              <CardContent>
                {resellerPermissions.customerSupport ? (
                  <div className="space-y-4">
                    <p className="text-gray-600">
                      Manage customer conversations and provide support for your referrals.
                    </p>
                    
                    <div className="grid gap-4">
                      {customerChats.map((chat) => (
                        <div key={chat.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold">
                              {chat.customerName.split(' ').map(n => n[0]).join('')}
                            </div>
                            <div>
                              <h4 className="font-semibold">{chat.customerName}</h4>
                              <p className="text-sm text-gray-600">{chat.lastMessage}</p>
                              <p className="text-xs text-gray-500">{new Date(chat.timestamp).toLocaleString()}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-3">
                            <Badge variant={chat.status === 'active' ? 'default' : 'secondary'}>
                              {chat.status}
                            </Badge>
                            <Button size="sm">
                              <MessageSquare className="h-4 w-4 mr-1" />
                              Respond
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-green-900 mb-2">Support Tips</h4>
                      <div className="text-sm text-green-800 space-y-1">
                        <p>• Respond quickly to build trust with your customers</p>
                        <p>• Use the AI assistant to help with common questions</p>
                        <p>• Escalate complex issues to the main support team</p>
                        <p>• Track your response times and customer satisfaction</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Support Access Restricted</h3>
                    <p className="text-gray-600">Your administrator has disabled customer support features. Contact support to enable this feature.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Reseller Settings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold mb-4">Account Permissions</h3>
                    <div className="space-y-3">
                      {Object.entries(resellerPermissions).map(([permission, enabled]) => (
                        <div key={permission} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <h4 className="font-medium capitalize">{permission.replace(/([A-Z])/g, ' $1').trim()}</h4>
                            <p className="text-sm text-gray-600">
                              {permission === 'customizeTheme' && 'Customize your store appearance and branding'}
                              {permission === 'manageSocialMedia' && 'Connect and manage social media accounts'}
                              {permission === 'viewAnalytics' && 'Access detailed performance analytics'}
                              {permission === 'customerSupport' && 'Provide direct customer support'}
                              {permission === 'createContent' && 'Create and modify marketing content'}
                              {permission === 'modifyPricing' && 'Set custom pricing for your customers'}
                              {permission === 'accessReports' && 'Generate and download business reports'}
                              {permission === 'teamManagement' && 'Manage your referral team'}
                            </p>
                          </div>
                          <Badge variant={enabled ? 'default' : 'secondary'}>
                            {enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-yellow-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-yellow-900 mb-2">Permission Notice</h4>
                    <p className="text-sm text-yellow-800">
                      Your permissions are controlled by the system administrator. 
                      Contact support if you need additional access or have questions about your current permissions.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* AI Chat Widget */}
      <AIChat 
        isOpen={chatOpen} 
        onToggle={() => setChatOpen(!chatOpen)}
        userType="reseller"
        resellerId={reseller?.id}
      />
    </div>
  );
};

export default EnhancedResellerDashboard;